import { message, notification } from "ant-design-vue"
import axios from "axios"

import { BACKOFFICE_IS_BLOCKED_ERROR_CODE } from "@/consts/errorCodes"
import i18n from "@/i18n"
import {
  CLOSE_ORDER_IN_DAY_OF_SHIPPING,
  INVALID_TTN_FORMAT,
  TTN_IS_ALREADY_USED
} from "@/modules/orders/errorCodes"
import router from "@/router"
import authenticationService from "@/services/authenticationService"
import { LOGOUT } from "@/store/modules/auth/actionTypes"
import getCurrentLocale from "@/utils/getCurrentLocale"

const API = axios.create({
  baseURL: `${new URL(process.env.VUE_APP_BASE_URL).origin}/api`,
  timeout: 60000,
  headers: {
    "Accept-Language": getCurrentLocale()
  },
  withCredentials: true
})

const optionalAPIConfig = (url, suffix) =>
  url
    ? { baseURL: `${new URL(url).origin}${suffix}` }
    : { adapter: () => Promise.reject(new axios.Cancel("Service disabled")) }

const ADS_API = axios.create({
  ...optionalAPIConfig(process.env.VUE_APP_ADS_BASE_URL, "/api"),
  timeout: 60000,
  headers: {
    "Accept-Language": getCurrentLocale()
  },
  withCredentials: true
})

const TM_API = axios.create({
  ...optionalAPIConfig(process.env.VUE_APP_TM_BASE_URL, ""),
  timeout: 60000,
  headers: {
    "Accept-Language": getCurrentLocale()
  },
  withCredentials: true
})

const PM_API = axios.create({
  baseURL: `${new URL(process.env.VUE_APP_PRODUCT_MODERATION_URL).origin}/api/v3/sellers`,
  timeout: 60000,
  headers: {
    "Accept-Language": "ru"
  },
  withCredentials: true
})

export async function getRequestInterceptor(config) {
  if (!config.baseURL) return config

  if (
    config.url.includes(authenticationService.REFRESR_TOKEN_API_URL) ||
    config.url.includes(authenticationService.LOGOUT_API_URL) ||
    config.url.includes("/v2/backoffice/auth/login")
  )
    return config

  const newToken = await this.refreshToken()

  if (newToken) {
    const jwtToken = `Bearer ${newToken}`

    config.headers["Authorization"] = jwtToken
  }

  return config
}

const normalizeMessages = (value) => {
  if (Array.isArray(value)) return value.filter((item) => typeof item === "string" && item)

  return typeof value === "string" && value ? [value] : []
}

export const normalizeAPIErrorData = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data

  if (!data.details || typeof data.details !== "object" || Array.isArray(data.details)) return data

  const normalized = { ...data }
  const generalMessages = normalizeMessages(data.details._general)

  if (!normalized.detail && generalMessages.length) {
    normalized.detail = generalMessages
  }

  if (data.type === "SCHEMA") {
    Object.entries(data.details).forEach(([field, messages]) => {
      if (!(field in normalized)) normalized[field] = normalizeMessages(messages)
    })
  }

  return normalized
}

const displayErrorDetail = (target, data) => {
  if (!data?.detail) return

  const detail = Array.isArray(data.detail) ? data.detail.join(", ") : data.detail

  if (detail) target(detail)
}

export function getResponseInterceptor(err) {
  if (err?.config?.url?.includes(authenticationService.LOGOUT_API_URL)) return Promise.reject(err)

  const self = this

  return new Promise(function () {
    if (err?.response?.data) {
      err.response.data = normalizeAPIErrorData(err.response.data)
    }

    const ignoreErrorsWithCodes = [
      CLOSE_ORDER_IN_DAY_OF_SHIPPING,
      INVALID_TTN_FORMAT,
      TTN_IS_ALREADY_USED,
      BACKOFFICE_IS_BLOCKED_ERROR_CODE
    ]

    const ignoreError =
      err?.response?.data?.code &&
      typeof err?.response?.data?.code === "string" &&
      ignoreErrorsWithCodes.some((errorCode) => err.response.data.code?.includes(errorCode))

    if (ignoreError) throw err

    switch (err?.response?.status) {
      case 400:
        if (self.$route.name !== "sign-in") {
          displayErrorDetail((detail) => self.$message.error(detail), err.response.data)
        }

        break

      case 401:
        self[LOGOUT]()
        break

      case 413:
        self.$message.error(self.$t("common.request_entity_too_large"))
        break

      case 500:
        if (!self.$route.path.includes("500"))
          self.$router.push({ path: "500", query: { from: self.$route.path } })

        break

      case 404:
      case 502:
        self.$router.push(`/${err.response.status}`)
        break

      default:
        if (err?.response?.status >= 400 && err.response.status < 500) {
          displayErrorDetail((detail) => self.$message.error(detail), err.response.data)
        }

        break
    }

    throw err
  })
}

export function getADSResponseInterceptor(err) {
  return new Promise(function () {
    if (err?.response?.data) {
      err.response.data = normalizeAPIErrorData(err.response.data)
    }

    switch (err?.response?.status) {
      case 400:
        displayErrorDetail((detail) => message.error(detail), err.response.data)
        break

      case 413:
        message.error(i18n.t("common.request_entity_too_large"))
        break

      case 500:
        const path = router.currentRoute.path

        if (!path.includes("500")) router.push({ path: "500", query: { from: path } })

        break

      default:
        if (err?.response?.status >= 400 && err.response.status < 500) {
          displayErrorDetail((detail) => message.error(detail), err.response.data)
        }

        break
    }

    throw err
  })
}

/** @param {import("axios").AxiosError<import("@/types/api/contracts").ApiErrorResponse>} error */
export const getModerationResponseInterceptor = (error) => {
  if (error?.config?.customConfig?.ignoreInterceptor) return Promise.reject(error)

  const data = error?.response?.data

  if (!data) {
    notification.error({ message: i18n.t("common.error") })
    return Promise.reject(error)
  }

  const { details } = data

  if (!details) {
    notification.error({ message: i18n.t("common.error") })
    return Promise.reject(error)
  }

  const generalMessages = Array.isArray(details) ? details : details._general || []
  generalMessages.forEach((text) => notification.error({ message: text }))

  return Promise.reject(error)
}

export { ADS_API, API, PM_API, TM_API }
